import * as React from 'react';
import { find, uniq, without } from 'lodash';

import { iCategoryType, iCategory } from '@/interfaces/categories';
import { List } from '@/components/List';
import { Select } from '@/components/Select';
import { treeService } from '@/services';

export interface iProps {
    /**
     * The category types that populate the dropdown.
     */
    categoryTypes: iCategoryType[];

    /**
     * Function called when the selected categories are changed.
     */
    onChange: (selectedCategoryIds: number[]) => any;

    /**
     * The currently selected categories.
     */
    selectedCategoryIds: number[];
}

// Extended category interface that includes extra fields we need for the UI.
interface iGroupedCategory extends iCategory {
    parentCategoryName: string;
    label: string;
}

/**
 * Allows the user to select one or more categories.
 *
 * @param {iProps} props
 * @returns {React.ReactElement}
 */
export const SelectCategories: React.SFC<iProps> = (props: iProps) => {
    if (!props.categoryTypes) { return null; }

    /**
     * Hook definitions.
     */
    const [categories, setCategories] = React.useState<iGroupedCategory[]>([]);

    /**
     * Re-group categories state whenever the props change.
     */
    React.useEffect(() => {
        // Pulls categories out of each category type and into one array
        const allCategoriesReducer = (prev: iCategory[], current: iCategoryType) => {
            return [
                ...prev,
                ...(current.categories || []).map((cat: iGroupedCategory) => {
                    cat.parentCategoryName = current.name;
                    cat.label = cat.name;
                    return cat;
                }),
            ];
        };

        // Get all categories
        const flattenedCategories = props.categoryTypes.reduce(allCategoriesReducer, []) as iGroupedCategory[];

        // Add label fields to each category
        const withLabels: iGroupedCategory[] = flattenedCategories.map(cat => {
            cat.label = treeService.buildPathLabel(cat, flattenedCategories, { itemLabelKey: 'name' });
            return cat;
        });

        setCategories(withLabels);
    }, [props.categoryTypes]);

    /**
     * Selects a category.
     * Calls "onChange" prop with updated list of selected items.
     *
     * @param {iCategory} category
     * @returns {void}
     */
    function selectCategory(category: iCategory): void {
        props.onChange(uniq([...props.selectedCategoryIds, category.id]));
    }

    /**
     * Unselects a category.
     * Calls "onChange" prop with updated list of selected items.
     *
     * @param {React.MouseEvent<HTMLButtonElement>} event
     * @param {iCategory} category
     * @returns {void}
     */
    function unselectCategory(event: React.MouseEvent<HTMLButtonElement>, category: iCategory): void {
        event.preventDefault();
        props.onChange(without([...props.selectedCategoryIds], category.id));
    }

    /**
     * Renders a single category item in the list.
     *
     * @param {iCategory} category
     * @returns {React.ReactElement}
     */
    function renderSelectedCategoryListItem(category: iCategory): React.ReactElement {
        if (!category) { return null; }

        return (
            <div className="list__item list-item">
                {treeService.buildPathLabel(category, categories, { itemLabelKey: 'name' })}

                <div className="list-item__controls">
                    <button
                        className="button--icon list-item__control"
                        onClick={event => unselectCategory(event, category)}
                    >
                        <i className="fa fa-trash button__icon"></i>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="select-categories">
            <label htmlFor="">Categories</label>
            <Select<iGroupedCategory>
                groupBy="parentCategoryName"
                items={categories.filter(cat => !props.selectedCategoryIds.includes(cat.id))}
                onSelect={selectCategory}
            />

            <List<iGroupedCategory>
                className="select-categories__selected"
                groupBy="parentCategoryName"
                items={props.selectedCategoryIds.map(id => find(categories, { id }))}
                renderItem={renderSelectedCategoryListItem}
            />
        </div>
    );
};
SelectCategories.displayName = 'SelectCategories';
SelectCategories.defaultProps = {
    categoryTypes: [],
    onChange: () => {
        throw new Error('"onChange" prop missing from <SelectCategories>');
    },
    selectedCategoryIds: [],
};
